/*
 * Reports of errors and notable events to the operator's log (F-68, ADR-0184). A report carries
 * technical values only: a kind from a closed list, the type name of an error (never its message),
 * an error code and a route template. The server checks each against the same patterns.
 */

/** Severity of a report. */
export type ClientLogLevel = "Information" | "Warning" | "Error";

/** What a report is about; the closed list of the server (`ClientLogEvent`). */
export type ClientLogEvent =
  | "AppStarted"
  | "UncaughtError"
  | "UnhandledRejection"
  | "RenderFailed"
  | "ScreenFailed"
  | "ServiceWorkerRegistrationFailed"
  | "ResponseInvalid"
  | "QueryFailed"
  | "SyncFailed";

/** Platform the app runs on; the same values as the source of clock events. */
export type ClientLogPlatform = "Web" | "Pwa" | "Ios" | "Android";

/** One report as the API receives it. */
export interface ClientLogEntry {
  /** Severity. */
  readonly level: ClientLogLevel;
  /** What happened. */
  readonly event: ClientLogEvent;
  /** Type name of the error, or `null`. */
  readonly errorName: string | null;
  /** Error code of the API or HTTP status, or `null`. */
  readonly errorCode: string | null;
  /** Route template of the screen, or `null`. */
  readonly route: string | null;
  /** How often the report occurred since the last delivery. */
  readonly count: number;
}

/** Body of `POST /api/v1/client-logs`. */
export interface ClientLogBatch {
  /** Always the app; the website reports on its own. */
  readonly source: "App";
  /** Platform of this device. */
  readonly platform: ClientLogPlatform;
  /** Version of this build. */
  readonly appVersion: string;
  /** One to {@link maxBatchSize} reports. */
  readonly entries: readonly ClientLogEntry[];
}

/** Details of a report; each is reduced to a technical value before it is sent. */
export interface ClientLogDetails {
  /** The error; only its type name is sent. */
  readonly error?: unknown;
  /** An error code of the API, an HTTP status or another technical code. */
  readonly errorCode?: string | null;
  /** A path; only its route template is sent. The current page if omitted. */
  readonly path?: string;
}

/** Reports errors and notable events. Reporting never throws and never waits. */
export interface ClientLog {
  /**
   * Reports a notable but expected event.
   *
   * @param event - What happened.
   * @param details - Optional details.
   */
  info(event: ClientLogEvent, details?: ClientLogDetails): void;
  /**
   * Reports a failure the app recovered from.
   *
   * @param event - What happened.
   * @param details - Optional details.
   */
  warn(event: ClientLogEvent, details?: ClientLogDetails): void;
  /**
   * Reports a failure the person noticed.
   *
   * @param event - What happened.
   * @param details - Optional details.
   */
  error(event: ClientLogEvent, details?: ClientLogDetails): void;
}

/** Outcome of a delivery: delivered (or refused for good), or to be tried again later. */
export type ClientLogDelivery = "delivered" | "dropped" | "retry";

/** Dependencies and settings of the {@link ClientLogger}. */
export interface ClientLoggerOptions {
  /** Platform of this device. */
  readonly platform: ClientLogPlatform;
  /** Version of this build; see {@link appVersionOf}. */
  readonly appVersion: string;
  /** Delivers a batch to the API. */
  readonly deliver: (batch: ClientLogBatch) => Promise<ClientLogDelivery>;
  /** The path of the current page (`location.pathname`). */
  readonly currentPath: () => string;
  /** Timer abstraction (injected for deterministic tests). */
  readonly schedule: (callback: () => void, delayMs: number) => () => void;
  /** Delay between the first pending report and its delivery; 5 seconds by default. */
  readonly flushDelayMs?: number;
  /** Delay before a failed delivery is tried again; 60 seconds by default. */
  readonly retryDelayMs?: number;
}

/** Most reports per request (`ClientLogService.MaxEntries` of the server). */
export const maxBatchSize = 20;

/** Most different reports kept while they wait for delivery; further ones are dropped. */
export const maxPending = 50;

/** Most reports of one page session; a fault that repeats endlessly stops being reported. */
export const maxReportsPerSession = 200;

/** Highest count of one report (`ClientLogService.MaxCount` of the server). */
const maxCount = 1000;

const identifier = /^[A-Za-z_$][A-Za-z0-9_$]{0,63}$/u;
const technicalCodePattern = /^[A-Za-z0-9][A-Za-z0-9._-]{0,39}$/u;
const appVersionPattern = /^[0-9A-Za-z][0-9A-Za-z.+_-]{0,39}$/u;
const wordSegment = /^[a-z][a-z-]{0,39}$/u;
const uuidSegment = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const dateSegment = /^\d{4}-\d{2}-\d{2}$/u;
const numberSegment = /^\d+$/u;
const maxRouteSegments = 12;

/**
 * Returns the type name of an error, never its message, which may hold personal data.
 *
 * @param error - Anything thrown or rejected.
 * @returns `TypeError`, `ApiError`, `AbortError` and so on, or the JavaScript type of a value that
 *   is no error (`string`, `object`).
 */
export function describeError(error: unknown): string {
  // Errors and DOMException (no subclass of Error in every engine) carry their type in `name`.
  const named =
    typeof error === "object" && error !== null && "name" in error && typeof error.name === "string"
      ? error.name
      : null;
  const name = named !== null && identifier.test(named) ? named : typeof error;

  return name;
}

/**
 * Returns a technical code as the server accepts it.
 *
 * @param code - An API error code (`TT-1001`), an HTTP status or another code.
 * @returns The code, or `null` if it is missing or not a technical code.
 */
export function technicalCode(code: string | null | undefined): string | null {
  const accepted =
    code !== null && code !== undefined && technicalCodePattern.test(code) ? code : null;

  return accepted;
}

/**
 * Returns the version of the build as the server accepts it.
 *
 * @param configured - The configured version (`VITE_APP_VERSION`).
 * @returns The version, or `dev` if none or an invalid one is configured.
 */
export function appVersionOf(configured: string | undefined): string {
  const version =
    configured !== undefined && appVersionPattern.test(configured) ? configured : "dev";

  return version;
}

/**
 * Replaces each part of a path that is no plain word with a placeholder, so no identifier, date
 * or typed text is reported: `/admin/employees/0193…` becomes `/admin/employees/:id`.
 *
 * @param path - A path without query and fragment (`location.pathname`).
 * @returns The route template.
 */
export function routeTemplate(path: string): string {
  const segments = path
    .split("/")
    .filter((segment) => segment !== "")
    .slice(0, maxRouteSegments)
    .map((segment) => {
      let template = ":x";

      if (wordSegment.test(segment)) {
        template = segment;
      } else if (uuidSegment.test(segment)) {
        template = ":id";
      } else if (dateSegment.test(segment)) {
        template = ":date";
      } else if (numberSegment.test(segment)) {
        template = ":n";
      }

      return template;
    });
  const trailingSlash = segments.length > 0 && path.endsWith("/");
  const route = `/${segments.join("/")}${trailingSlash ? "/" : ""}`;

  return route;
}

/** A report that does nothing, for tests and screens without a provider. */
export const silentClientLog: ClientLog = {
  info: (): void => undefined,
  warn: (): void => undefined,
  error: (): void => undefined,
};

/** A pending report with its key for counting repetitions. */
interface PendingReport {
  readonly key: string;
  readonly entry: ClientLogEntry;
}

/**
 * Collects reports and delivers them in batches a few seconds after the first one: the same report
 * is counted instead of sent again, at most {@link maxPending} different reports wait, and a page
 * session sends at most {@link maxReportsPerSession}. A delivery that fails for lack of a
 * connection is tried again later; reports are not stored on the device.
 */
export class ClientLogger implements ClientLog {
  private readonly options: ClientLoggerOptions;
  private pending: PendingReport[] = [];
  private inFlight: ReadonlySet<PendingReport> = new Set();
  private reported = 0;
  private cancelFlush: (() => void) | null = null;
  private flushing = false;

  /**
   * Creates the logger.
   *
   * @param options - Dependencies and settings.
   */
  constructor(options: ClientLoggerOptions) {
    this.options = options;
  }

  /** @inheritdoc */
  info(event: ClientLogEvent, details: ClientLogDetails = {}): void {
    this.add("Information", event, details);
  }

  /** @inheritdoc */
  warn(event: ClientLogEvent, details: ClientLogDetails = {}): void {
    this.add("Warning", event, details);
  }

  /** @inheritdoc */
  error(event: ClientLogEvent, details: ClientLogDetails = {}): void {
    this.add("Error", event, details);
  }

  /**
   * Delivers the pending reports now, for example when the page is hidden.
   *
   * @returns A promise that resolves when the delivery ended; it never rejects.
   */
  async flush(): Promise<void> {
    this.cancelFlush?.();
    this.cancelFlush = null;

    if (this.flushing) {
      return;
    }

    this.flushing = true;

    try {
      await this.deliverPending();
    } finally {
      this.flushing = false;
    }
  }

  private add(level: ClientLogLevel, event: ClientLogEvent, details: ClientLogDetails): void {
    if (this.reported >= maxReportsPerSession) {
      return;
    }

    let path: string;

    try {
      path = details.path ?? this.options.currentPath();
    } catch {
      path = "/";
    }

    const entry: ClientLogEntry = {
      level,
      event,
      errorName: details.error === undefined ? null : describeError(details.error),
      errorCode: technicalCode(details.errorCode),
      route: routeTemplate(path),
      count: 1,
    };
    const key = [entry.level, entry.event, entry.errorName, entry.errorCode, entry.route].join("|");
    // A report on its way is not counted again; a repetition waits as a report of its own.
    const index = this.pending.findIndex(
      (report) => report.key === key && !this.inFlight.has(report),
    );
    const existing = index < 0 ? undefined : this.pending[index];

    if (existing !== undefined) {
      const counted = { ...existing.entry, count: Math.min(existing.entry.count + 1, maxCount) };
      this.pending[index] = { key, entry: counted };
    } else if (this.pending.length < maxPending) {
      this.pending.push({ key, entry });
    } else {
      return;
    }

    this.reported += 1;
    this.scheduleFlush(this.options.flushDelayMs ?? 5000);
  }

  private scheduleFlush(delayMs: number): void {
    if (this.cancelFlush !== null) {
      return;
    }

    this.cancelFlush = this.options.schedule(() => {
      this.cancelFlush = null;
      void this.flush();
    }, delayMs);
  }

  private async deliverPending(): Promise<void> {
    let outcome: ClientLogDelivery = "delivered";

    while (this.pending.length > 0 && outcome !== "retry") {
      const batch = this.pending.slice(0, maxBatchSize);
      this.inFlight = new Set(batch);
      const body: ClientLogBatch = {
        source: "App",
        platform: this.options.platform,
        appVersion: this.options.appVersion,
        entries: batch.map((report) => report.entry),
      };

      try {
        outcome = await this.options.deliver(body);
      } catch {
        outcome = "dropped";
      }

      if (outcome !== "retry") {
        // Reports added while the request ran stay pending; only the delivered ones are removed.
        const sent = this.inFlight;
        this.pending = this.pending.filter((report) => !sent.has(report));
      }

      this.inFlight = new Set();
    }

    if (outcome === "retry") {
      this.scheduleFlush(this.options.retryDelayMs ?? 60_000);
    }
  }
}
